import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Radio, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Operational admin screen -> English
const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24 && now.getDate() === then.getDate()) return `${hrs}h ago`;
  const days = Math.floor(mins / 1440);
  if (days < 7) return then.toLocaleDateString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const RecentlyActiveCard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchRecent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/recent-activity`);
      setClients(res.data?.clients || []);
    } catch (e) {
      console.error('recent-activity fetch failed', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchRecent();
    const t = setInterval(fetchRecent, 60000); // refresh once a minute
    return () => clearInterval(t);
  }, [fetchRecent]);

  return (
    <div className="glass-card p-5" data-testid="recently-active-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
          <Radio className="w-5 h-5 text-emerald-300" /> Recently active
        </h3>
        <span className="text-xs text-blue-300/60">last 7 days</span>
      </div>

      {loading ? (
        <p className="text-blue-300/70 text-sm py-3 text-center">Loading…</p>
      ) : clients.length === 0 ? (
        <p className="text-blue-300/70 text-sm py-3 text-center" data-testid="recently-active-empty">
          No client activity yet — this fills in as clients open the app.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="recently-active-list">
          {clients.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/admin/clients/${c.id}`)}
              className="flex items-center justify-between gap-2 bg-slate-800/50 hover:bg-slate-700/60 rounded-lg px-3 py-2 text-left transition"
              data-testid={`recently-active-${c.id}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className={`w-2 h-2 rounded-full shrink-0 ${c.online ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
                <span className="text-white text-sm font-medium truncate">{c.full_name}</span>
              </span>
              <span className="flex items-center gap-1 shrink-0">
                <span className={`text-xs ${c.online ? 'text-emerald-300' : 'text-blue-300/60'}`}>
                  {c.online ? 'online' : relTime(c.last_seen_at)}
                </span>
                <ChevronRight className="w-4 h-4 text-blue-300/40" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentlyActiveCard;
